import { Link } from "react-router-dom";
import { FileText, CheckCircle, TrendingUp, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAdminDashboard } from "@/hooks/api/use-admin";
import { useAuth } from "@/contexts/AuthContext";

const AdminDashboard = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  
  // Admin-specific data
  const { data: adminDashboardData, isLoading: adminDashboardLoading } = useAdminDashboard({ enabled: !!user });

  const stats = [
    {
      title: t('dashboard.totalTenders') || 'إجمالي المناقصات',
      value: adminDashboardData?.data?.total_tenders?.toString() || "0",
      icon: FileText,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      title: t('dashboard.activeTenders'),
      value: adminDashboardData?.data?.active_tenders?.toString() || "0",
      icon: CheckCircle,
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      title: t('dashboard.totalClients') || 'إجمالي العملاء',
      value: adminDashboardData?.data?.total_clients?.toString() || "0",
      icon: Users,
      color: "text-amber-600",
      bgColor: "bg-amber-100",
    },
    {
      title: t('dashboard.totalOffers') || 'إجمالي العروض',
      value: adminDashboardData?.data?.total_offers?.toString() || "0",
      icon: TrendingUp,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
  ];

  
  if (adminDashboardLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-4 rounded" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-12" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <div className={`p-2 rounded ${stat.bgColor}`}>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="cursor-pointer hover:shadow-md transition-shadow">
          <Link to="/admin/dashboard/clients" className="block p-6">
            <div className="flex items-center space-x-3">
              <Users className="h-8 w-8 text-primary" />
              <div>
                <h3 className="font-semibold">{t('dashboard.manageClients')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('dashboard.viewManageClients')}
                </p>
              </div>
            </div>
          </Link>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow">
          <Link to="/admin/dashboard/tenders" className="block p-6">
            <div className="flex items-center space-x-3">
              <FileText className="h-8 w-8 text-primary" />
              <div>
                <h3 className="font-semibold">{t('dashboard.manageTenders')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('dashboard.viewManageTenders')}
                </p>
              </div>
            </div>
          </Link>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow">
          <Link to="/admin/dashboard/content" className="block p-6">
            <div className="flex items-center space-x-3">
              <FileText className="h-8 w-8 text-primary" />
              <div>
                <h3 className="font-semibold">{t('dashboard.manageContent')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('dashboard.viewManageContent')}
                </p>
              </div>
            </div>
          </Link>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
